/* ===========================================================================
 * THE NEW-RING PURCHASE PANEL. Loaded only on the new-ring template, from
 * snippets/product-form.liquid. Approved design "Website V5 - Mobile.jpg",
 * 13 October 2026.
 *
 * WHAT IT OWNS
 *   - the two Inside Engraving cards (Text, Fingerprint) and what they open
 *   - the ring sizer card's selected look
 *   - Crafting Time, and keeping it in step with the chosen variant's ladder
 *   - the timeline under Add to cart
 *   - moving the spec table into the Specs section (moveSpecs, near the end)
 *   - GETTING UP TO FOUR LINES INTO THE CART IN ONE PRESS
 *
 * THE CART PATH, AND IT WAS CHOSEN BY MEASUREMENT, NOT BY PREFERENCE.
 * Up to three lines can go in at once: the ring (with its properties and maybe a
 * file), the Expedite line and the fingerprint fee line. THE RING SIZER IS NOT A
 * LINE ANY MORE (17 October 2026, docs/BACKLOG.md B26): its tick box is
 * properties[Ring Sizer] = 3-Pack on the ring itself - see snippets/pdp-upsell.liquid -
 * so it rides in with the ring by either route and nothing here adds it. It was a
 * fourth line, variant 50359840571696, until that day.
 * A file only travels in a multipart request, and the theme's own add-to-cart
 * serialises with theme.utils.serialize, which SKIPS <input type="file"> - so
 * the photograph would post as nothing. Two routes were open. This one was run
 * for real against this store on 13 October 2026 before any of it was built:
 *
 *   1. POST /cart/add.js with a multipart FormData holding the ring's id,
 *      quantity, every properties[...] and the file.
 *        MEASURED: 200, and the property came back as a real link -
 *        "Inside Fingerprint":
 *        "https://cdn.shopify.com/s/files/1/2531/8450/uploads/54fa0a7e....png"
 *   2. POST /cart/add.js again with JSON {items:[...]} for the bare extra lines.
 *        MEASURED: 200. /cart.js then showed three lines - the ring at $449
 *        with all four properties, Expedite at $99, and the sizer at $30 with
 *        discount FREEKIT:3000 and final_line_price 0, which is where "FREE"
 *        on the card comes from.
 *   THE OTHER ANSWER WAS ALSO MEASURED, so the test above could have failed:
 *   both calls with an impossible variant id answered 422 "Cannot find variant"
 *   and the cart stayed empty.
 *
 * WITH NO FILE AND NO EXTRA LINES THIS DOES NOTHING AT ALL and the theme's own
 * add-to-cart runs untouched. That is deliberate: do not "simplify" it by always
 * taking over.
 *
 * HOW IT TAKES OVER. A capture-phase submit listener on `document`, which is the
 * only place that is reliably BEFORE theme.AjaxProduct's own listener. That one
 * is bound on the form itself, and for an event whose target IS the form both
 * capture and bubble listeners there fire in registration order - so binding on
 * the form, capture or not, would be a race. It is not a race on `document`.
 *
 * WHAT IS KNOWN AND NOT SOLVED HERE, because solving it needs Shopify Plus:
 *   - removing a ring from the cart later leaves its Expedite and fee lines
 *     behind; they are separate lines with nothing linking them. (The sizer used
 *     to be left behind too, until it became a property on the ring.)
 *   - two rings with the same extra merge that extra into one line of quantity 2.
 * =========================================================================== */
(function () {
  'use strict';

  if (window.__sestoRingPdp) return;
  window.__sestoRingPdp = true;

  var DAY = 86400000;
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MAX_FILE_BYTES = 20 * 1024 * 1024;   // 20 MB, with a plain-words refusal

  /* ---------------------------------------------------------------- small */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ----------------------------------------------------------- the dates */

  // THE WORKSHOP'S TODAY, not the customer's. en-CA gives YYYY-MM-DD, which
  // parses without ambiguity; the explicit zone is what stops a visitor in
  // Sydney being shown a date a day out. If Intl refuses the zone the date is
  // left off the timeline rather than guessed - the same choice
  // ring-delivery-date.liquid made.
  function benchToday() {
    try {
      var s = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
      var p = s.split('-');
      return Date.UTC(+p[0], +p[1] - 1, +p[2]);
    } catch (e) {
      return null;
    }
  }

  function iso(ms) { return new Date(ms).toISOString().slice(0, 10); }

  // NOTHING CALLS THE DATE FUNCTIONS BELOW SINCE 16 OCTOBER 2026, and they are
  // kept on purpose. Step one of the timeline used to print the sizer's ship date
  // ("Oct 19th"); Lorenzo's mockup replaced it with "Next day" / "Same day".
  // These, with the closure list in snippets/ring-pdp-timeline.liquid, are the
  // only written record of the shop's own sizer rule, and the date may come back.

  // THE DAY THE SIZER CAN ACTUALLY GO IN THE POST: the next day the workshop is
  // open AFTER today. It always moves at least one day forward - that is the
  // shop's own rule for a sizer order, not an off-by-one - and it steps over
  // Saturdays, Sundays and the closure list.
  function nextOpenDay(ms, closed) {
    ms += DAY;
    for (var i = 0; i < 60; i++) {
      var d = new Date(ms).getUTCDay();
      if (d !== 0 && d !== 6 && !closed[iso(ms)]) return ms;
      ms += DAY;
    }
    return ms;
  }

  function ordinal(n) {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    if (n % 10 === 1) return 'st';
    if (n % 10 === 2) return 'nd';
    if (n % 10 === 3) return 'rd';
    return 'th';
  }

  function sayDate(ms) {
    var d = new Date(ms);
    return MON[d.getUTCMonth()] + ' ' + d.getUTCDate() + ordinal(d.getUTCDate());
  }

  /* ------------------------------------------------------- reading state */

  function formOf(el) { return el.closest('form[action*="/cart/add"]'); }


  function sizerBox(root) {
    // THE SIZER IS FOUND BY ID PREFIX, NOT BY CLASS, AND THAT IS ON PURPOSE.
    // snippets/pdp-upsell.liquid has drawn it three ways now - .pdp-upsell__checkbox
    // on the classic style, .sesto-upsell__check on the first card style, and
    // .sp-sizer__check here. All three carry id="pdp-upsell-<block id>", so the
    // one selector finds whichever style the template is set to.
    return $('[id^="pdp-upsell-"]', root) || $('[id^="pdp-upsell-"]');
  }

  function speedOf(root) {
    var paid = $('[data-sp-speed="paid"]', root);
    return paid && paid.checked ? 'paid' : 'free';
  }

  /* ----------------------------------------------------------- the panel */

  function Panel(root) {
    this.root = root;
    this.form = formOf(root);
    this.craft = $('[data-sp-craft]', root);
    this.ladders = readJson($('[data-ring-ladders]', root));
    this.key = this.ladders && this.ladders.current ? this.ladders.current : 'standard';
    this.tl = $('[data-sp-timeline]', root);
    this.engrave = $('[data-sp-engrave]', root);
    // 'required' = the ring's OWN outside print (Design_Fingerprint rings, free),
    // 'fee'      = the +$150 inside add-on,
    // 'none'     = no fingerprint card at all.
    // snippets/ring-pdp-engraving.liquid decides it; nothing here guesses.
    this.printMode = this.engrave ? (this.engrave.getAttribute('data-print-mode') || 'none') : 'none';
    this.closed = {};
    (readJson($('[data-sp-closed]', root)) || []).forEach(function (d) { this.closed[d] = true; }.bind(this));
    this.busy = false;
    this.locked = false;          // set after a partial add, so a retry cannot double-add

    /* WHICH SIDE EACH ENGRAVING GOES ON. Lorenzo, 17 October 2026: "OK lets do it",
       and then, on the greyed-out side, "feels like the app does not work because
       one expects to click on outside but nothing happens" - so where only one side
       is allowed there is now NO SWITCH, just a label (option A, his choice).
       null  = the product's metafield said nothing, so there is no switch and no word
               on the page. snippets/ring-pdp-engraving.liquid still draws the field for
               it, and paintSide() sends it as Inside - see THE ORDER below.
       'Inside' / 'Outside' = the side this engraving goes on.

       WHERE IT IS READ FROM, and it is never decided here:
         'choose'            - off the markup, whichever segment is aria-checked.
         'inside'/'outside'  - OFF THE MODE ITSELF. There is no control in a label,
                               so there is nothing to read; the mode IS the answer.
       snippets/ring-pdp-engraving.liquid turns the metafield into that mode and
       snippets/ring-pdp-side.liquid draws one or the other, so the default lives in
       one place and this only follows it. Both cases carry the same [data-sp-side]
       wrapper, which is why there is one lookup here and not two. */
    this.side = { text: null, print: null };
    ['text', 'print'].forEach(function (which) {
      var group = $('[data-sp-side="' + which + '"]', root);
      if (!group) return;
      var mode = group.getAttribute('data-sp-side-mode');
      if (mode === 'inside' || mode === 'outside') {
        this.side[which] = mode === 'outside' ? 'Outside' : 'Inside';
        return;
      }
      var on = $('[data-sp-side-opt][aria-checked="true"]', group);
      this.side[which] = on ? on.getAttribute('data-sp-side-opt') : 'Inside';
    }.bind(this));
  }

  /* ------------------------------------------------------------ timeline */

  Panel.prototype.paintTimeline = function () {
    var tl = this.tl;
    if (!tl) return;

    var on = !!(sizerBox(this.root) || {}).checked;
    var speed = speedOf(this.root);
    var L = this.ladders && this.ladders.ladders ? this.ladders.ladders[this.key] : null;
    var lead = L ? (speed === 'paid' ? L.leadPaid : L.leadFree) : '';

    var order = on ? ['sizer', 'confirm', 'make', 'ship'] : ['make', 'ship'];
    tl.setAttribute('data-steps', String(order.length));
    // The pill's colours and the "Order before 2 PM ET" line are CSS off this.
    tl.setAttribute('data-speed', speed);

    $$('[data-sp-step]', tl).forEach(function (step) {
      var name = step.getAttribute('data-sp-step');
      var at = order.indexOf(name);
      step.hidden = at < 0;
      step.classList.toggle('is-first', at === 0);
      step.classList.toggle('has-line', at > 0);
      var num = $('[data-sp-num]', step);
      if (num && at >= 0) num.textContent = String(at + 1);
    });

    // STEP ONE: when the sizer goes out. Lorenzo's own words, 16 October 2026.
    var whenOut = $('[data-sp-sizer-when]', tl);
    if (whenOut) whenOut.textContent = speed === 'paid' ? 'Same day' : 'Next day';

    // STEP THREE: the crafting time, which is what leadFree / leadPaid have
    // always said. It sat on the last step until 17 October 2026.
    var leadOut = $('[data-sp-lead]', tl);
    if (leadOut) leadOut.textContent = lead || '';

    // STEP FOUR: which service, not how long. The customer's own choice read
    // back to them, off the same radio the switch moves.
    var speedWord = $('[data-sp-speedword]', tl);
    if (speedWord) speedWord.textContent = speed === 'paid' ? 'Priority' : 'Standard';

    /* THE BOTTOM LINE NAMES THE TIMELINE, AND MOVES WITH THE SWITCH. Lorenzo,
       17 October 2026, with a picture of the panel off and on: "Change bottom line
       as shown". His two lines are:
         Priority off -> "Standard timeline - Business days"
         Priority on  -> "Priority timeline - Business days"
       (a middle dot, with a space either side, where the dash is written here).

       "BUSINESS DAYS" IS STILL ONLY SAID WHERE IT IS TRUE, which is the one rule
       this line has carried through every rewrite of it. The shop counts the gold
       and custom ladders in CALENDAR days, so the STANDARD ladder says it and no
       other does - "Only where it's true", 16 October. On gold and custom the line
       now reads just "Standard timeline" or "Priority timeline", WHICH ALSO FILLS
       A LINE THAT USED TO BE EMPTY THERE: before today those two ladders said
       nothing at all here and the CSS held the empty line's height.

       IT IS THE SAME LINE IN BOTH STATES OF THE PANEL - the two-step one and the
       four-step one with the sizer. His picture is of the two-step panel, but the
       sentence is about the timeline the customer chose, which is true either way,
       and one line that changes meaning with the sizer would be worse. Said in the
       handover so he can say otherwise. */
    var foot1 = $('[data-sp-foot-1]', this.root);
    if (foot1) {
      var timelineWord = speed === 'paid' ? 'Priority timeline' : 'Standard timeline';
      foot1.textContent = this.key === 'standard'
        ? timelineWord + ' · Business days'
        : timelineWord;
    }
  };

  /* ----------------------------------------------------- crafting ladder */

  // THE LABEL AND THE ID MOVE TOGETHER. A gold sleeve buys the $149 Expedite and
  // a wooden one the $99; a button that said one price while the id bought the
  // other would charge the wrong amount. The radio VALUES move too, because they
  // are what reaches properties[_Production Time] and the OMS reads them.
  Panel.prototype.applyLadder = function (key) {
    var d = this.ladders;
    if (!d || !d.ladders || !d.ladders[key] || !this.craft) return;
    var L = d.ladders[key];
    this.key = key;

    var free = $('[data-sp-speed="free"]', this.craft);
    var paid = $('[data-sp-speed="paid"]', this.craft);
    var fee = $('[data-sp-fee]', this.craft);
    if (!free || !paid) return;

    free.value = L.free;
    paid.value = L.paid + ' (Expedited)';
    if (fee) fee.textContent = L.fee || '';
    this.craft.setAttribute('data-expedite-variant', L.id || '');

    // Fail closed: a ladder with no resolvable Expedite variant shows no
    // Expedited button, and a ticked Expedited falls back to Standard.
    var box = $('.sp-speed', this.craft);
    var freeOnly = !L.id;
    if (box) box.classList.toggle('sp-speed--free-only', freeOnly);
    // And the switch goes with it: a ladder that cannot charge must not show
    // something to turn on.
    this.craft.classList.toggle('sp-craft--free-only', freeOnly);
    if (freeOnly && paid.checked) { free.checked = true; }
    this.paintSpeed();
  };

  // THE SWITCH IS PAINTED FROM THE RADIO, NEVER THE OTHER WAY ROUND. The radio is
  // what reaches properties[_Production Time], so it is the state; aria-checked is
  // a reading of it, and the CSS dresses the switch off that same attribute, so
  // what is on screen and what is in the order cannot drift apart.
  Panel.prototype.paintSpeed = function () {
    var on = speedOf(this.root) === 'paid';
    var sw = $('[data-sp-priority]', this.root);
    if (sw) sw.setAttribute('aria-checked', on ? 'true' : 'false');
    /* THE WHOLE PANEL GOES CREAM WITH A GOLD OUTLINE WHILE PRIORITY IS ON - the
       same selected look a chosen card wears. TAKEN FROM LORENZO'S PICTURE OF
       17 October 2026, NOT FROM ANYTHING HE WROTE: he drew the panel that way in
       the "on" half of it and said nothing about it in words. Flagged in the
       handover so it can be taken out again in one line if he did not mean it.
       The class is toggled here because nothing else on the panel watches the
       radio; what it draws is in snippets/ring-pdp-styles.liquid. */
    if (this.craft) this.craft.classList.toggle('is-priority', on);
    this.paintTotal();   // Priority on or off, or a new ladder's price
  };

  /* -------------------------------------------------------- sizer & cards */

  /* ===================== THE SIZE CHECK =====================
     Lorenzo's spec, 17 October 2026: "show contextual guidance only when a customer
     attempts to add a ring to their cart without selecting a size".

     THE WORDS ARE CHOSEN AT THE MOMENT OF THE PRESS, from whether the sizing kit is
     ticked, because the two cases are answering different questions: with a kit on
     its way a size is a STARTING POINT and the customer is told why one is needed at
     all; without a kit it is simply the size they will be sent.

     ONE ELEMENT, REUSED. Pressing three times cannot stack three messages, and the
     element is empty and `hidden` at page load so the Size section is no taller than
     it was until the guidance is really needed.

     NOTHING HERE IS RED. The border is the page's own gold and the words are the gold
     ink: "The message should look like helpful guidance rather than a harsh red
     error." */
  Panel.prototype.sizeSelect = function () {
    return this.form ? this.form.elements['properties[Size]'] : null;
  };

  /* THE SIZE BOX IS NOT INSIDE [data-sp-root], AND THAT CAUGHT ME OUT. MEASURED on
     the page: the select, the sizing-kit tick and this message all sit in the theme's
     own .variant-wrapper, outside the `.sp` wrapper the panel is rooted at - which is
     the same reason the select reaches the form through form="..." rather than by
     being inside it. A lookup scoped to the root finds nothing at all, and the first
     build of this quietly did nothing when the button was pressed.

     So it is looked for in the root first and then in the document, exactly as
     sizerBox() above has always done for the kit's tick box. */
  Panel.prototype.sizeMsgEl = function () {
    return $('[data-sp-size-msg]', this.root) || $('[data-sp-size-msg]');
  };

  /* TWO SENTENCES AGAIN, ONE FOR EACH CASE. Lorenzo first asked for one sentence
     whatever is ticked (24 October 2026), then, the same week: "can you change it
     back to what it was for with ring sizer selected". So with the Guaranteed Fit
     kit ticked the warning again says which size to pick and what the kit sends;
     without the kit it is the plain size sentence. The kit's tick-box listener
     calls sizeFlag(true, false), which repaints these words when the kit is ticked
     or unticked while the warning shows, without raising a new warning. */
  Panel.prototype.sizeWords = function () {
    var kit = !!(sizerBox(this.root) || {}).checked;
    return kit
      ? 'Please choose your closest ring size to continue. We’ll send 3 ring sizers: '
        + 'your selected size, ¼ down, and ¼ up.'
      : 'Please choose your ring size to continue.';
  };

  /* WHAT THE CUSTOMER ACTUALLY SEES WHERE THE SIZE IS CHOSEN. The <select> is
     held at opacity 0 behind the custom picker's button (snippets/ring-pdp-
     options.liquid dresses it and marks the wrapper [data-sp-pick]); until it
     has, the select itself is the box. This answers whichever is really on
     screen, so the shake shakes something a customer can see. */
  Panel.prototype.sizeBoxEl = function () {
    var sel = this.sizeSelect();
    if (!sel) return null;
    var wrap = sel.closest ? sel.closest('[data-sp-pick]') : null;
    var btn = wrap ? wrap.querySelector('[data-sp-pick-btn]') : null;
    return btn || sel;
  };

  /* ONE SHORT SHAKE, AND ONLY IF THE PHONE ALLOWS MOVEMENT. The class is taken
     off first and the box measured, which is what makes the browser treat a
     second press as a new animation instead of ignoring it; animationend takes
     it off again, and a timer does the same in case the animation never runs. */
  Panel.prototype.shake = function (el) {
    if (!el || !el.classList) return;
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    } catch (err) { /* no matchMedia: shake, as every browser did before it existed */ }
    el.classList.remove('sp-shake');
    void el.offsetWidth;
    el.classList.add('sp-shake');
    var off = function () {
      el.classList.remove('sp-shake');
      el.removeEventListener('animationend', off);
    };
    el.addEventListener('animationend', off);
    window.setTimeout(off, 900);
  };

  /* ONE REFUSAL, ONE SHAPE - 24 October 2026. Lorenzo: "what about the other
     required fields? it should be the same for all the fields that are required."

     Every field that stops Add to cart goes through here, so the size, the
     engraving and the fingerprint all behave identically: the phone buzzes on the
     press, the page travels to the field, and the field shakes once when it lands.
     The red words and the red edge are the caller's own - each field has its own
     sentence and its own box - and this is everything they share.

     THE BUZZ IS FIRST AND DOES NOT WAIT ON THE ELEMENT, because the press has been
     refused whether or not there is anything to scroll to. */
  Panel.prototype.refuse = function (el) {
    buzz();
    if (!el) return;
    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    catch (err) { el.scrollIntoView(); }
    var self = this;
    whenStill(el, function () { self.shake(el); });
  };

  /* THE UPLOAD BOX A CUSTOMER CAN SEE. The file input lies over it at opacity 0,
     so the box - not the input - is what wears the red edge and what shakes. */
  Panel.prototype.printBoxEl = function () {
    var input = $('[data-sp-print]', this.root);
    var box = input && input.closest ? input.closest('.sp-drop') : null;
    return box || $('.sp-drop', this.root);
  };

  /* WAIT FOR THE SCROLL TO COME TO REST. sizeFlag scrolls with behavior:'smooth',
     and a shake that starts while the page is still moving is not seen. Nothing
     here listens for a scroll event - the page can be scrolled by the customer
     at the same time - it simply watches the box's own top until it has stopped
     changing for three frames, and gives up after 1.2s so the shake always
     happens even on a browser that never settles. */
  function whenStill(el, cb) {
    if (!el || !window.requestAnimationFrame) { window.setTimeout(cb, 400); return; }
    var last = null;
    var same = 0;
    var start = Date.now();
    (function tick() {
      var y = Math.round(el.getBoundingClientRect().top);
      if (y === last) same++; else { same = 0; last = y; }
      if (same >= 3 || Date.now() - start > 1200) { cb(); return; }
      window.requestAnimationFrame(tick);
    })();
  }

  /* A SHORT BUZZ ON THE PRESS, WHERE THE BROWSER ALLOWS ONE. Lorenzo, 24 October
     2026: "can we vibrate their phone at all?" On Android, yes - this runs inside
     the press, which is the user gesture Chrome requires. ON AN IPHONE, NO:
     Safari gives a web page no vibration at all and there is no way round it, so
     the guard below simply finds nothing to call and the page behaves as it did.
     Nothing else depends on it; a browser that throws is caught and ignored. */
  function buzz() {
    try {
      if (window.navigator && typeof window.navigator.vibrate === 'function') {
        window.navigator.vibrate([18, 40, 18]);
      }
    } catch (err) { /* a browser that refuses is not an error here */ }
  }

  /* WHAT THE ADD TO CART BUTTON SAYS WHILE NO SIZE IS CHOSEN. Lorenzo, 24 October
     2026: the button and the sticky bar read "Choose your size" with no price,
     and go back to the theme's own words and the total the moment a size is
     picked. snippets/product-form.liquid says it from the first paint so the
     button never flashes "Add to cart $249" before this script arrives.

     IT DOES NOT WRITE THE LABEL FROM paintTotal(), AND THAT IS DELIBERATE. There
     is a MutationObserver on this very element that calls paintTotal() whenever
     the theme rewrites it; a paintTotal that wrote here would call itself for
     ever. So this writes, and then calls paintTotal once - and it writes only
     when the words really differ, so the observer settles after one pass.

     IT NEVER OVERWRITES "SOLD OUT" OR "UNAVAILABLE". It only ever swaps between
     the theme's own default text and these three words; anything else on the
     button belongs to the theme and is left exactly as it found it. paintTotal()
     then hides the total by itself, because it already refuses to show one
     unless the label reads the default. */
  Panel.prototype.SIZE_LABEL = 'Choose your size';

  Panel.prototype.sizeChosen = function () {
    var sel = this.sizeSelect();
    if (!sel) return true;              // not a ring with a size: nothing to ask for
    return !!String(sel.value || '').trim();
  };

  Panel.prototype.paintAtcLabel = function () {
    var form = this.form;
    if (!form || !this.sizeSelect()) return;
    var label = form.querySelector('[data-add-to-cart-text]');
    if (!label) return;
    var def = String(label.getAttribute('data-default-text') || '').trim();
    if (!def) return;
    var now = label.textContent.trim();
    /* ALWAYS "ADD TO CART" AGAIN, 25 October 2026. Lorenzo: "the add to cart
       button should alwasy say add to cart as before. Do not say chooe size".
       So this only ever takes "Choose your size" OFF the button - a page
       cached before today can still be carrying it - and never puts it on.
       Pressing with no size still asks for one: that is sizeFlag(), not this. */
    if (now !== this.SIZE_LABEL) return;
    var want = def;
    if (now === want) return;
    label.textContent = want;
    this.paintTotal();                  // the price and the sticky bar follow the words
  };

  /* Shows or clears the guidance. `move` is true only for the press itself: when the
     customer ticks or unticks the kit WHILE the message is up, its words change to
     match but the page does not jump and nothing is flagged afresh - he asked for no
     NEW warning from the tick. */
  Panel.prototype.sizeFlag = function (on, move) {
    var sel = this.sizeSelect();
    var msg = this.sizeMsgEl();
    if (!sel || !msg) return;
    if (on) {
      msg.textContent = this.sizeWords();
      msg.hidden = false;
      sel.classList.add('is-flagged');
      sel.setAttribute('aria-invalid', 'true');
      if (msg.id) sel.setAttribute('aria-describedby', msg.id);
      if (move) {
        try { sel.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        catch (err) { sel.scrollIntoView(); }
        // preventScroll, because the smooth scroll above is already on its way and
        // focus would otherwise jump the page there instantly and fight it.
        try { sel.focus({ preventScroll: true }); } catch (err2) { sel.focus(); }
        /* AND THE SHAKE, ONCE THE SCROLL HAS LANDED. Not before: a box that
           shakes while the page is still travelling is not seen. The size list
           is NOT opened - Lorenzo, 24 October 2026: "do all you suggested
           except opening the size dropdown". The box that shakes is the one the
           customer can see: the picker's button once it has been built, and the
           select itself until then. */
        var self = this;
        var box = this.sizeBoxEl();
        whenStill(box, function () { self.shake(box); });
      }
    } else {
      msg.textContent = '';
      msg.hidden = true;
      sel.classList.remove('is-flagged');
      sel.removeAttribute('aria-invalid');
      sel.removeAttribute('aria-describedby');
    }
  };

  Panel.prototype.sizeFlagged = function () {
    var msg = this.sizeMsgEl();
    return !!(msg && !msg.hidden);
  };

  /* ------------------------------------------------------------------ */
  /* WHAT THE LASER CANNOT CUT. Lorenzo, 18 October 2026: "can we connect the text
     field on the product page to validate if we can engrave the characters that are
     entered?"

     THE RULE IS NOT HERE AND MUST NOT BE COPIED HERE. assets/sp-engrave-check.js is
     Shopify Theme Waqas's file and is the same one the cart loads, so a character the
     cart refuses is refused here in the same words. This asks it and shows its answer.

     IT IS ASKED LAZILY AND NEVER CAPTURED: window.SestoEngrave is read at the moment of
     the question, so the deferred script may still be arriving, may be taken off the
     page, or may throw - and in every one of those cases this answers '' and the page
     behaves exactly as it did before the check existed. A checker that fails must never
     be able to wedge the cart.

     WHAT IS CHECKED IS WHAT WILL BE POSTED - NOT WHAT IS ON SCREEN. The first build asked
     whether the Text area was open, and that was wrong: CLOSING THE CARD DOES NOT CLEAR
     THE BOX. MEASURED before this fix - "I ★ U" typed, the Engraving card tapped shut,
     Add to cart pressed - the page did not refuse and the form still held
     properties[Text] = "I ★ U".

     The page already answers the real question in one place. paintText() takes the `name`
     OFF this input whenever the box is empty and puts it back when it is not, because a
     nameless control is posted by NEITHER route - the theme's own serialize and the
     multipart post here both walk form.elements and skip it. So a named, non-empty box is
     exactly the text that will reach the order, card open or shut, and that is the test:

       empty box            nameless by paintText   not checked, not blocked
       text, card open      named                   checked
       text, card SHUT      still named             CHECKED, which it was not before

     paintText() settles that name near its top and calls this at its end, and submit()
     runs paintText() before anything reads the form, so the name is never stale here. */
  Panel.prototype.engraveMsgEl = function () {
    return $('[data-sp-text-bad]', this.root) || $('[data-sp-text-bad]');
  };

  Panel.prototype.engraveWords = function () {
    var input = $('[data-sp-text]', this.root);
    if (!input || !input.value) return '';
    if (!input.getAttribute('name')) return '';
    var api = window.SestoEngrave;
    if (!api || typeof api.message !== 'function') return '';
    // NOT normalise(). The page posts exactly what was typed; what the order system
    // stores is the order system's business, and rewriting it here would mean the
    // customer saw one engraving and bought another.
    try { return api.message(input.value) || ''; }
    catch (err) { return ''; }
  };

  /* Shows or clears the sentence. `move` is true only for the press itself, exactly as
     sizeFlag's is - typing gets the words without the page jumping under the fingers.

     THE BOX IS NEVER FOCUSED, and that is not an oversight. Lorenzo's rule, which the
     cart follows too: the keyboard must not open by itself. sizeFlag does focus its
     select because a select opens no keyboard; a text box does, and it would cover the
     very sentence the customer has just been sent to read.

     The words are only written when they have changed, because the line is aria-live
     and rewriting the same sentence makes a screen reader say it twice. */
  Panel.prototype.engraveFlag = function (words, move) {
    var msg = this.engraveMsgEl();
    var input = $('[data-sp-text]', this.root);
    if (!msg) return;
    if (words) {
      if (msg.textContent !== words) msg.textContent = words;
      msg.hidden = false;
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        if (msg.id) input.setAttribute('aria-describedby', msg.id);
        /* AND THE BOX ITSELF SAYS SO - 24 October 2026. The sentence under the
           box used to be the only sign; the box now wears the same red edge and
           tint as the size box, drawn by snippets/sp-warn-look.liquid. */
        input.classList.add('is-flagged');
      }
      if (move && input) {
        /* THE SAME REFUSAL THE SIZE GETS: the buzz, the scroll, and one shake
           when it lands. THE BOX IS STILL NEVER FOCUSED - Lorenzo's rule, which
           the cart follows too: the keyboard must not open by itself, and here it
           would cover the very sentence the customer has been sent to read. */
        this.refuse(input);
      }
    } else {
      if (msg.textContent) msg.textContent = '';
      msg.hidden = true;
      if (input) {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
        input.classList.remove('is-flagged');
      }
    }
  };

  Panel.prototype.paintSizer = function () {
    var box = sizerBox(this.root);
    var wrap = box && box.closest('.sp-sizer');
    if (wrap) wrap.classList.toggle('is-on', !!box.checked);
  };

  Panel.prototype.openArea = function (which, open) {
    var area = $('[data-sp-open="' + which + '"]', this.root);
    var card = $('[data-sp-card="' + which + '"]', this.root);
    if (area) area.hidden = !open;
    if (card) card.setAttribute('aria-expanded', open ? 'true' : 'false');
    // A refusal message is for the moment it was given: closing the area clears it,
    // so it cannot reappear later beside a good photo.
    if (which === 'print' && !open) this.printError('');

    // THE ONE INVARIANT THAT KEEPS THE FORM FROM WEDGING: on a ring whose
    // fingerprint is required, `required` is on the input ONLY while this area
    // is open. A hidden required control cannot be focused, and a form holding
    // one refuses to submit with nothing on screen to say why - Chrome logs
    // "An invalid form control is not focusable" and stops dead. With the area
    // shut the input is plain, the form is valid, the submit fires, and
    // submit() below does the refusing where the customer can see it.
    /* `required` IS NOT PUT ON THE UPLOAD AT ALL ANY MORE - 24 October 2026, and
       it is a removal that makes the page simpler, not riskier.

       WHY IT WAS THERE: a hidden required control wedges a form silently - Chrome
       says "An invalid form control is not focusable", the submit never happens
       and nothing on screen explains it - so the attribute was put on only while
       the area was open and taken off when it shut, and submit() caught the empty
       case itself.

       WHY IT GOES: while it was on, the BROWSER refused the press before the
       submit event could reach this file, so the customer got Chrome's own bubble
       instead of the page's red sentence - and only sometimes, depending on
       whether the area happened to be open. submit() has always done the real
       refusing; now it does all of it, in one voice, every time.

       NOTHING IS LOOSENED. The attribute was added by this script and never
       appeared in snippets/ring-pdp-engraving.liquid, so a page whose JavaScript
       failed never had it in the first place: with the area shut - which is how
       every ring page loads - there was no `required` to rely on. The refusal
       that keeps a Design_Fingerprint ring out of the cart without its photograph
       is the one in submit(), and it is untouched.

       An attribute left over from an earlier build is taken off, once, here. */
    if (which === 'print') {
      var input = $('[data-sp-print]', this.root);
      if (input && input.hasAttribute('required')) input.removeAttribute('required');
    }
  };

  Panel.prototype.isOpen = function (which) {
    var area = $('[data-sp-open="' + which + '"]', this.root);
    return !!(area && !area.hidden);
  };

  // The Text card's "saved" look. The input is the form field either way, so
  // nothing is stored here and nothing can be lost; this only decides whether
  // the card wears a plus or a tick.
  //
  // IT DOES NOT SHOW THE WORDS BACK IN THE CARD any more - Lorenzo, 14 October
  // 2026. The tick is the whole signal; the words live in the box, which the
  // card reopens. See the note in snippets/ring-pdp-engraving.liquid.
  Panel.prototype.paintText = function () {
    var input = $('[data-sp-text]', this.root);
    var card = $('[data-sp-card="text"]', this.root);
    if (!input || !card) return;
    var value = input.value.trim();
    // Spaces are not an engraving. Clearing the box when what is left is only
    // whitespace keeps "   " out of properties[Text] and out of the order.
    if (!value && input.value) input.value = '';
    // NO EMPTY "Text" ON THE ORDER. The box keeps its name - properties[Text], the
    // name the OMS reads - only while it holds an engraving; with nothing in it the
    // name comes off, and a control with no name is never posted by either route.
    // Shopify Theme Waqas, 17 October 2026: the page sent `Text: ""` with every
    // ring, the cart removes the key when an engraving is removed, and a blank
    // "Text:" could show under the ring at checkout. The name is read once from the
    // markup and put back exactly.
    if (!this.textName) this.textName = input.getAttribute('name') || 'properties[Text]';
    if (input.value) input.setAttribute('name', this.textName);
    else input.removeAttribute('name');
    // ASKED ONCE, USED TWICE - the card's tick here and the sentence at the foot of this
    // function. After the `name` above, never before: engraveWords() only looks at text
    // that is really going to be posted, and the name is what decides that.
    var engraveBad = this.engraveWords();
    /* THE TICK MEANS SAVED, SO IT IS NOT WORN WHILE THE TEXT IS REFUSED. Shopify Theme
       Waqas, 18 October 2026, checking this on The Flourish: the card showed its gold
       tick directly above the line saying the character cannot be engraved, and the two
       contradicted each other. With something refused the card shows its plus instead,
       as if nothing had been saved yet. NO RED AND NO WARNING LOOK IS ADDED - the
       sentence under the box is the message, and this is only the tick going quiet.

       WITH NO CHECKER engraveWords() answers '' and this is exactly what it always was.

       NOTHING THE ORDER SEES CHANGES. extras() reads the Priority radio and the chosen
       file and never this class, so the posted lines and the button's total cannot move;
       the side tabs are painted from the input's value below, not from the card; and the
       refused engraving is still refused at Add to cart, which is where it is stopped. */
    card.classList.toggle('is-on', value.length > 0 && !engraveBad);
    // After the name above, never before: whether the side is sent depends on
    // whether an engraving is being sent at all.
    this.paintSide('text');
    var count = $('[data-sp-count]', this.root);
    if (count) {
      var max = parseInt(input.getAttribute('data-sp-max'), 10) || 40;
      var left = max - input.value.length;
      count.textContent = left + (left === 1 ? ' character left' : ' characters left');
    }
    // THE ENGRAVING CHECK RIDES ON paintText, which is the one place every path that
    // changes the text already comes through: the input event, Enter, blur, resync()
    // after Back, and the paintText() submit() runs before anything reads the form. So
    // the sentence appears as the character is typed and goes the moment it is deleted,
    // and there is nothing extra to wire or to forget.
    this.engraveFlag(engraveBad, false);
  };

  /* THE SIDE SWITCH, AND THE ONE PROPERTY IT MAY ADD TO THE ORDER.

     Two jobs, and the second is the one that matters:
       1. the look - which segment is cream with the gold line, and which side the
          Tab key reaches (roving tabindex, the way a radio group works).
       2. THE ORDER. `properties[Text Side]` / `properties[Print Side]` is sent
          WHENEVER the engraving is really being sent - a non-empty text box, or a
          chosen photo - and says which side, `Inside` or `Outside`: the chosen
          side is written into the field before it is named. Lorenzo, 20 October
          2026, looking at an order that said no side at all: "When an order is placed it should always mention if the text was inside or outside engraving and same for the image".
          Until then only `Outside` was ever sent and an absent property meant
          inside; an order from before that still reads that way.

     THE NAME GOES ON AND COMES OFF THE FIELD, which is the same technique the empty
     Text box already uses a few lines up: a control with no `name` is posted by
     NEITHER route - the theme's own add-to-cart (theme.utils.serialize) and the
     multipart post here (ringFormData) both walk form.elements and skip it. So the
     field is always in the form and the question is only ever whether it is named.
     The name itself is read from the markup (data-sp-side-name) and never written
     here, so the two provisional names live in one place.

     THESE TWO NAMES ARE PROVISIONAL. ERP Main and the Engraving session have still
     to agree them and the OMS does not read them today, so THE DRAFT THEME MUST NOT
     BE PUBLISHED until it does - there is already a publish hold on this draft
     (docs/BACKLOG.md B26). No existing property name is touched.

     CALLED FROM paintText AND paintPrint, not wired separately, so every path that
     already repainted an engraving settles its side too: the input event, the file
     change, Remove, resync() on DOMContentLoaded/load/pageshow (which is what keeps
     a browser's refill after Back from leaving a stale answer on the order), and the
     paintText() that submit() runs before anything reads the form. */
  Panel.prototype.paintSide = function (which) {
    var group = $('[data-sp-side="' + which + '"]', this.root);
    var field = $('[data-sp-side-field="' + which + '"]', this.root);
    if (!group && !field) return;          // no side at all on this engraving
    var chosen = this.side[which] || 'Inside';

    // A LABEL HAS NO SEGMENTS, so this loop simply finds none and falls through to
    // the field below - which is how "Outside only" still puts Outside on the order
    // with nothing on screen to click. No special case is needed for it.
    if (group) {
      $$('[data-sp-side-opt]', group).forEach(function (opt) {
        var on = opt.getAttribute('data-sp-side-opt') === chosen;
        opt.classList.toggle('is-on', on);
        opt.setAttribute('aria-checked', on ? 'true' : 'false');
        opt.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    if (!field) return;
    var sending = false;
    if (which === 'text') {
      // The same test paintText uses, and it runs after paintText has already
      // emptied a box holding nothing but spaces.
      var text = $('[data-sp-text]', this.root);
      sending = !!(text && text.value);
    } else {
      var print = $('[data-sp-print]', this.root);
      sending = !!(print && print.files && print.files.length);
    }
    var name = field.getAttribute('data-sp-side-name') || '';
    field.value = chosen;
    if (name && sending) field.setAttribute('name', name);
    else field.removeAttribute('name');
  };

  Panel.prototype.paintPrint = function () {
    var input = $('[data-sp-print]', this.root);
    var card = $('[data-sp-card="print"]', this.root);
    var saved = $('[data-sp-saved="print"]', this.root);
    if (!input || !card) return;
    var has = !!(input.files && input.files.length);
    var name = has ? input.files[0].name : '';
    card.classList.toggle('is-on', has);
    if (saved) {
      saved.textContent = name;
      saved.hidden = !has;
    }
    // The open area shows the chosen photo's name with Change and Remove, and the
    // dashed box steps aside (Lorenzo, 14 October 2026).
    var area = $('[data-sp-open="print"]', this.root);
    if (area) area.classList.toggle('has-file', has);
    this.paintTotal();   // a print photo chosen or removed adds or takes off the fee
    var chosen = $('[data-sp-chosen]', this.root);
    if (chosen) chosen.hidden = !has;
    var chosenName = $('[data-sp-chosen-name]', this.root);
    if (chosenName) chosenName.textContent = name;
    // A photo chosen or removed decides whether the print's side reaches the order.
    this.paintSide('print');
  };

  // PUTS A FILE BACK INTO THE INPUT. Chrome empties a file input when its picker
  // is cancelled, which made a chosen photo vanish; the last good file is kept on
  // the panel and restored. Returns false where a browser will not allow it.
  Panel.prototype.restorePrint = function (input, file) {
    if (!input || !file || typeof DataTransfer === 'undefined') return false;
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      return !!(input.files && input.files.length === 1);
    } catch (err) {
      return false;
    }
  };

  Panel.prototype.clearPrint = function () {
    var input = $('[data-sp-print]', this.root);
    if (input) { input.value = ''; }
    var err = $('[data-sp-print-error]', this.root);
    if (err) { err.textContent = ''; err.hidden = true; }
    this.paintPrint();
  };

  /* THE PHOTOGRAPH'S SENTENCE, AND THE BOX WITH IT - 24 October 2026. The words
     were already here; the red edge and tint on the box are new, so this refusal
     looks like the size's and the engraving's. One call clears both. */
  Panel.prototype.printError = function (message) {
    var err = $('[data-sp-print-error]', this.root);
    var box = this.printBoxEl();
    if (box && box.classList) {
      if (message) box.classList.add('is-flagged');
      else box.classList.remove('is-flagged');
    }
    if (!err) return;
    err.textContent = message;
    err.hidden = !message;
  };

  /* --------------------------------------------------------------- cart */

  Panel.prototype.note = function (message) {
    var box = $('[data-sp-cart-note]', this.root);
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
    /* AND IT IS BROUGHT INTO VIEW. This note sits by the real Add to Cart; a press
       from the sticky bar can be a screen or more below it, and a customer who is
       told nothing presses again. */
    if (message) {
      try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      catch (err) { box.scrollIntoView(); }
    }
  };

  Panel.prototype.extras = function () {
    var out = [];
    var craft = this.craft;
    var paid = craft && $('[data-sp-speed="paid"]', craft);
    if (paid && paid.checked) {
      var eid = craft.getAttribute('data-expedite-variant');
      if (eid) out.push({ id: Number(eid), quantity: 1, what: 'Priority', grouped: true });
    }
    // NO RING SIZER LINE. Since 17 October 2026 the sizer is a property on the
    // ring - properties[Ring Sizer] = 3-Pack, the tick box's own name and value,
    // which ringFormData() and the theme's own add-to-cart both pick up through
    // form.elements. Lorenzo: "can we change the ring sizer from being a product
    // to same as the engraving text?" (docs/BACKLOG.md B26).
    // The fingerprint fee only ever goes in when a file is actually chosen AND
    // the fee variant exists. With no variant the card is in its review state
    // and nothing is charged - see snippets/ring-pdp-engraving.liquid.
    var print = $('[data-sp-print]', this.root);
    var feeId = this.engrave && this.engrave.getAttribute('data-fee-variant');
    if (this.printMode === 'fee' && feeId && print && print.files && print.files.length) {
      out.push({ id: Number(feeId), quantity: 1, what: 'the fingerprint engraving', grouped: true });
    }
    return out;
  };

  /* THE TOTAL ON ADD TO CART. Lorenzo, 17 October 2026, relayed by Shopify Theme Waqas: "can
     we show the total price in the add to cart button after the text add to cart - $1000? This
     price updates as the customer adds or removes add ons?"

     IT IS BUILT FROM extras() ITSELF - the very list submit() is about to post - so the price on
     the button and what lands in the cart cannot drift apart: the ring variant's own price,
     then each extra line's price looked up by its id. Priority's price is the ladder's own
     `cents` and the fingerprint fee's is data-fee-cents, both read off the variants in Liquid,
     never parsed out of the "+$149" words. The free engraving text and the free "Ring Sizer:
     3-Pack" are not lines, so they add nothing. Discount codes (COMBO50 and the rest) belong to
     the cart and are not taken off here.

     NO TOTAL RATHER THAN A WRONG ONE: if any price is missing, or the theme is saying Sold Out
     or Unavailable instead of "Add to cart", the span is emptied and hidden. Formatted by the
     theme's own theme.Currency.formatMoney with the shop's money format, which is how the
     price beside the title is written ("$1,430.00"). */
  Panel.prototype.centsOf = function (id) {
    var d = this.ladders || {};
    var key;
    for (key in (d.ladders || {})) {
      var L = d.ladders[key];
      if (L && L.id && String(L.id) === String(id)) return typeof L.cents === 'number' ? L.cents : null;
    }
    if (this.engrave && String(this.engrave.getAttribute('data-fee-variant')) === String(id)) {
      var fee = parseInt(this.engrave.getAttribute('data-fee-cents'), 10);
      return isNaN(fee) ? null : fee;
    }
    return null;
  };

  /* The chosen variant's OWN price, with nothing added. totalCents() below is this
     plus the extras, so the difference between them is what the add-ons come to.

     NOTHING CALLS THIS ANY MORE. Its one caller was paintCompare, removed on
     19 October 2026 with paintTopPrice; it is left because it is the only place that
     reads a single variant's price out of the ladders block, and the next thing that
     needs that should use it rather than write it again. */
  Panel.prototype.ringCents = function () {
    var d = this.ladders;
    if (!d || !d.prices) return null;
    var idField = this.form && this.form.querySelector('[name="id"]');
    var id = this.variantId || (idField && idField.value);
    var v = d.prices[String(id)];
    return typeof v === 'number' ? v : null;
  };

  Panel.prototype.totalCents = function () {
    var d = this.ladders;
    if (!d || !d.prices) return null;
    var idField = this.form && this.form.querySelector('[name="id"]');
    var id = this.variantId || (idField && idField.value);
    var total = d.prices[String(id)];
    if (typeof total !== 'number') return null;
    var self = this;
    var known = true;
    this.extras().forEach(function (x) {
      var c = self.centsOf(x.id);
      if (c === null) known = false;
      else total += c * (x.quantity || 1);
    });
    return known ? total : null;
  };

  Panel.prototype.paintTotal = function () {
    var form = this.form;
    var out = form && form.querySelector('[data-sp-atc-total]');
    if (!out) return;
    var btn = this.button();
    var label = form.querySelector('[data-add-to-cart-text]');
    var normal = !!label && label.textContent.trim().toLowerCase()
      === String(label.getAttribute('data-default-text') || '').trim().toLowerCase();
    var fmt = window.theme && window.theme.Currency && window.theme.Currency.formatMoney;
    var money = window.theme && window.theme.settings && window.theme.settings.moneyFormat;
    var cents = normal && fmt && money ? this.totalCents() : null;
    this.paintTerms(cents, fmt, money);
    // THE TOP PRICE IS NOT TOUCHED. It is the ring's own price and the theme owns it.
    // It used to be written here with the total, from a design review of 17 October
    // 2026 - "My preference is to update both prices dynamically" - and on 19 October
    // Lorenzo found what that costs on a ring that is on sale: "if the ring is on sale
    // meaning compare at price is higher when adding priority or fingerprint the price
    // of the ring goes up and the compare at price disappears". A compare-at price only
    // means anything beside the ring's own price, so a top price that follows the total
    // forces the struck price and the OFF badge to be hidden, and the sale goes with
    // them. The total belongs to the button, the sticky bar and the Shop Pay line
    // below - all three still follow it, and all three say what will be charged.
    if (cents === null) {
      out.textContent = '';
      out.hidden = true;
      if (btn) btn.classList.remove('sp-atc--total');
      this.paintSticky();
      return;
    }
    out.textContent = fmt(cents, money);
    out.hidden = false;
    if (btn) btn.classList.add('sp-atc--total');
    this.paintSticky();
  };

  /* paintTopPrice() AND paintCompare() WERE HERE, AND ARE GONE - 19 October 2026.

     paintTopPrice wrote the add-ons total into [data-product-price], the price at
     the top of the page. paintCompare then had to hide what no longer matched it:
     it put `sp-sale-off` on [data-product-price-wrap] (the struck compare-at) and
     on [data-save-price], and `sp-badge-off` on the gallery wrapper (the "18% OFF"
     corner badge).

     Lorenzo, 19 October 2026: "if the ring is on sale meaning compare at price is
     higher when adding priority or fingerprint the price of the ring goes up and
     the compare at price disappears". MEASURED on The Sanctum at 1280, Priority on:
     the top price read $548.00 against the ring's $449.00, and both the $549.00
     struck price and the "18% OFF" badge went to visibility:hidden.

     Nothing replaced them. The header is the theme's again, start to finish, which
     is why the fix is a removal and not a rewrite: there is no second writer left
     to disagree with the theme about what the ring costs.

     The total did not go anywhere - paintTotal still puts it on the button, paintSticky
     mirrors it on the sticky bar, and paintTerms hands it to Shopify's instalments
     element. Those three are the whole of what shows a total now. */

  /* THE STICKY BAR IS A MIRROR. It shows the real button's words, its total and its
     disabled state, and nothing else; a tap on it clicks the real button, so there is
     still one add-to-cart path and every check on it runs. */
  Panel.prototype.paintSticky = function () {
    var bar = this.stickyBar();          // this panel's own, not the first on the page
    var real = this.button();
    if (!bar || !real || !this.form) return;
    var label = this.form.querySelector('[data-add-to-cart-text]');
    var realTot = this.form.querySelector('[data-sp-atc-total]');
    var txt = $('[data-sp-sticky-text]', bar);
    var tot = $('[data-sp-sticky-total]', bar);
    var btn = $('[data-sp-sticky-btn]', bar);
    if (txt && label) txt.textContent = label.textContent.trim();
    if (tot) {
      var has = !!(realTot && !realTot.hidden && realTot.textContent);
      tot.textContent = has ? realTot.textContent : '';
      tot.hidden = !has;
      if (btn) btn.classList.toggle('sp-sticky__btn--total', has);
    }
    if (btn) btn.disabled = !!real.disabled;
  };

  /* THE INSTALLMENTS LINE FOLLOWS THE SAME TOTAL. Lorenzo, 17 October 2026: "The paument price
     does not change as more add ons are added to the ring like print and priority, is that
     possible?" Shopify's element works the monthly figure out in the browser from the price in
     its `shopify-meta` attribute and Shopify's own plan rates (financing_plans, same attribute) -
     so the chosen variant's entry is given the button's total and SHOPIFY DOES THE ARITHMETIC.
     Nothing here computes a financing figure. MEASURED on The Sanctum before building: $549 ->
     "From $49.55/mo", given $798 -> "From $72.03/mo", original back -> $49.55.
     Built every time from the attribute as the page first drew it, so only the chosen variant's
     entry ever differs, and with no total (sold out, an unknown price) the original goes back.
     price_per_term is the total split evenly, which is what Shopify's interest-free split is. */
  Panel.prototype.paintTerms = function (cents, fmt, money) {
    var el = $('.sp-payment-terms shopify-payment-terms', this.root);
    if (!el) return;
    if (this.termsMeta === undefined) this.termsMeta = el.getAttribute('shopify-meta') || null;
    if (!this.termsMeta) return;
    var meta;
    try { meta = JSON.parse(this.termsMeta); } catch (err) { return; }
    if (cents !== null && fmt && money && meta.variants) {
      var idField = this.form && this.form.querySelector('[name="id"]');
      var id = String(this.variantId || (idField && idField.value) || el.getAttribute('variant-id'));
      meta.variants.forEach(function (v) {
        if (String(v.id) !== id) return;
        var per = Math.round(cents / (v.number_of_payment_terms || 4));
        v.full_price_amount = cents / 100;
        v.full_price = fmt(cents, money);
        v.price_per_term_amount = per / 100;
        v.price_per_term = fmt(per, money);
      });
    }
    var next = cents === null ? this.termsMeta : JSON.stringify(meta);
    if (el.getAttribute('shopify-meta') !== next) el.setAttribute('shopify-meta', next);
  };

  /* WHAT IS DRAWN IS WHAT THE BOXES HOLD, EVEN AFTER THE BROWSER HAS BEEN AT THEM.
     Lorenzo, 17 October 2026: "when landed on the page I selected priority. It did not enable
     the toggle but updated the timeline to show the confirm sizer but the ring sizer is not
     even added". REPRODUCED by ticking the sizer and Priority by hand, going to the cart and
     pressing Back: the browser put both back into their boxes AFTER start() had painted, and
     told nobody. The card showed "+", the switch showed off and the timeline two steps while
     the boxes held the opposite - so one tap on the switch turned Priority OFF and the
     timeline jumped to four steps. A photo chosen earlier came back too, which put $150 on
     the total with the Print card still showing "+".
     The page's own choices now carry autocomplete="off", so a browser should not put them back
     at all; this is the second half, for any browser that does it anyway: repaint everything
     from the boxes once the page has settled and whenever it is shown again. The ladder is
     re-read from the variant id the form will actually post, for the same reason. */
  Panel.prototype.resync = function () {
    var idField = this.form && this.form.querySelector('[name="id"]');
    var map = this.ladders && this.ladders.variants;
    if (idField && map && map[idField.value]) {
      this.variantId = idField.value;
      if (map[idField.value] !== this.key) this.applyLadder(map[idField.value]);
    }
    this.paintSizer();
    this.paintSpeed();
    this.paintText();
    this.paintPrint();
    // Both of those paint their own side already; said again here because this is
    // the Back-button path, and a switch showing one side while the order carries
    // the other is exactly the bug autocomplete="off" and this function exist for.
    this.paintSide('text');
    this.paintSide('print');
    this.paintTimeline();
    this.paintTotal();     // which repaints the top price and the sticky mirror too
    /* AND THE BUTTON'S WORDS MATCH THE SIZE BOX, WHATEVER THE PAGE CAME BACK
       HOLDING. This function is the Back-button path and runs at boot as well
       (DOMContentLoaded, load and pageshow all call it), so it covers both
       directions: a browser that refilled the size box after the Liquid said
       "Choose your size", and a size cleared behind the script's back. */
    this.paintAtcLabel();
  };

  /* THE GROUP TOKEN - `_sp_group`, backlog B25's theme contract (docs/BACKLOG.md).
     One random token per press of Add to Cart, written to a hidden field
     properties[_sp_group] in the ring's own form - so it reaches the ring line by
     BOTH routes: the theme's own add-to-cart when there is nothing extra, and the
     multipart post below (ringFormData reads form.elements) - and copied onto that
     same press's Priority (RSH) and fingerprint (NGRVNG) lines in the second post.
     It went on a ring sizer line too from 16 October 2026 until the next day, when
     the sizer stopped being a line at all and became properties[Ring Sizer] on the
     ring (backlog B26) - so there is no sizer line left to tag. The cart page (another session's, 16 October 2026) reads the
     token to draw the boxes; a cart transform app would use it to merge Priority and
     the fingerprint into the ring, if one is ever allowed on this plan. The leading underscore keeps it out of the customer's
     sight - snippets/cart-item.liquid skips such properties and checkout hides them -
     and no property the OMS reads (Size, Text, Inside Fingerprint, Need by) is touched.
     A NEW token every press, so two of the same ring stay two lines with their own
     extras rather than merging into one line of quantity 2. */
  Panel.prototype.groupToken = function () {
    var token = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    var form = this.form;
    if (!form) return token;
    var field = form.querySelector('input[name="properties[_sp_group]"]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[_sp_group]';
      form.appendChild(field);
    }
    field.value = token;
    return token;
  };

  Panel.prototype.hasFile = function () {
    return $$('input[type="file"]', this.form || this.root).some(function (el) {
      return el.files && el.files.length > 0;
    });
  };

  Panel.prototype.ringFormData = function () {
    var form = this.form;
    var fd = new FormData();
    var skip = { 'form_type': 1, 'utf8': 1, 'product-id': 1, 'section-id': 1, 'add': 1 };
    var sawQuantity = false;
    // form.elements, NOT querySelectorAll: Size is drawn in the variant picker
    // block, outside the form, and reaches it through form="...". Only
    // form.elements sees a control associated that way.
    Array.prototype.slice.call(form.elements).forEach(function (el) {
      if (!el.name || el.disabled || skip[el.name]) return;
      if (el.type === 'file') {
        if (el.files && el.files.length) fd.append(el.name, el.files[0]);
        return;
      }
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      if (el.name === 'quantity') sawQuantity = true;
      fd.append(el.name, el.value);
    });
    if (!sawQuantity) fd.append('quantity', '1');
    return fd;
  };

  Panel.prototype.button = function () {
    return this.form ? this.form.querySelector('[data-add-to-cart]') : null;
  };

  /* THIS PANEL'S OWN STICKY BAR. Kept from start(), because once the bar has been
     moved out of the section (which only happens when something above it would break
     position:fixed) it can no longer be found by looking inside the root. */
  Panel.prototype.stickyBar = function () {
    if (this.sticky && this.sticky.isConnected) return this.sticky;
    this.sticky = $('[data-sp-sticky]', this.root);
    return this.sticky;
  };

  Panel.prototype.loading = function (state) {
    var btn = this.button();
    if (!btn) return;
    btn.classList.toggle('btn--loading', !!state);
    btn.disabled = !!state;
    /* AND THE MIRROR WEARS IT TOO. A press from the sticky bar happens a screen below
       the real button, so without this the spinner is off screen and the bar looks
       dead while the cart is being reached. */
    var bar = this.stickyBar();
    var sb = bar && $('[data-sp-sticky-btn]', bar);
    if (sb) {
      sb.classList.toggle('btn--loading', !!state);
      sb.disabled = !!state || !!btn.disabled;
    }
  };

  Panel.prototype.goToCart = function () {
    var route = (window.theme && window.theme.routes && window.theme.routes.cartPage) || '/cart';
    window.location = route;
  };

  Panel.prototype.submit = function (e) {
    if (this.locked) { e.preventDefault(); e.stopPropagation(); return; }
    // Settle the engraving's name before anything reads the form: this listener runs
    // before the theme's own, so an empty box is nameless by the time it serialises.
    // paintText also settles the text's side; the print's is settled whenever a photo
    // is chosen or removed, and said again here so that neither name can be stale by
    // the time either route walks form.elements.
    this.paintText();
    this.paintSide('print');

    /* NO SIZE, NO ADD - AND THIS IS CHECKED BEFORE ANYTHING ELSE. Lorenzo's spec:
       "Do not send the product to Shopify's cart without a valid size." This listener
       is capture-phase on `document`, so it runs before the theme's own add-to-cart
       and before our multipart route; preventDefault and stopPropagation here mean
       NOTHING is posted by either of them.

       IT IS CHECKED BEFORE THE PRINT. A ring with neither a size nor its required
       photograph should ask for the size first, because that is the field the page
       scrolls to and focuses, and two refusals at once would fight over the scroll.

       EVERY ENTRY POINT COMES THROUGH HERE, including the sticky bar at the foot of
       a phone, because that button clicks the real one rather than posting anything
       of its own. */
    var sizeSel = this.sizeSelect();
    if (sizeSel && !String(sizeSel.value || '').trim()) {
      e.preventDefault();
      e.stopPropagation();
      /* THE BUZZ GOES FIRST, INSIDE THE PRESS. Chrome only allows a vibration
         from a real user gesture, and this listener is still inside the click.
         An iPhone has no vibration for a web page and buzz() finds nothing to
         call. The button also says "Choose your size" and shows no price while
         this is true - repainted here as well as on the size's own change, so
         a press can never leave the words behind. */
      buzz();
      this.paintAtcLabel();
      this.sizeFlag(true, true);
      return;
    }

    /* AND THEN WHAT THE LASER CANNOT CUT. Same refusal, same capture-phase listener, so
       neither the theme's add-to-cart nor our multipart route posts anything - and the
       sticky bar at the foot of a phone comes through here too, because it clicks the
       real button rather than posting on its own.

       SECOND OF THE THREE, between the size and the required print, because the size is
       the field the page already focuses and because the print branch below CLOSES the
       text area on its way to the upload box. One field at a time, each scrolled to in
       its turn.

       engraveWords() answers '' for an empty box or a checker that is not there, so in
       both the press falls straight through to the print check exactly as it did before.
       It does NOT answer '' for a shut Engraving card: the box keeps its text and its
       name behind a shut card, so that text is still posted and is still checked.

       WHICH IS WHY THE AREA IS OPENED FIRST. The sentence lives inside it, so refusing
       with the card shut would stop the press and show nothing. Opening it puts the
       words where they can be read; engraveFlag then scrolls the box into view, the same
       shape as the size check. NEITHER CALL FOCUSES ANYTHING - the keyboard must not
       open by itself. */
    var engraveBad = this.engraveWords();
    if (engraveBad) {
      e.preventDefault();
      e.stopPropagation();
      this.openArea('text', true);
      this.engraveFlag(engraveBad, true);
      return;
    }

    // THE REQUIRED FINGERPRINT IS REFUSED HERE, not by a hidden `required`.
    // This listener is capture-phase on `document`, so it runs before
    // theme.AjaxProduct and nothing is ever posted. The area is opened (which
    // puts `required` back on the input), scrolled to, and handed to
    // reportValidity(), so the browser parks its own plain message on the box
    // where the photo goes. The cart is not touched and the page does not move.
    if (this.printMode === 'required') {
      var needPrint = $('[data-sp-print]', this.root);
      if (needPrint && !(needPrint.files && needPrint.files.length)) {
        e.preventDefault();
        e.stopPropagation();
        this.openArea('text', false);
        this.openArea('print', true);
        /* OUR OWN WORDS NOW, NOT THE BROWSER'S BUBBLE - 24 October 2026.
           Lorenzo: "what about the other required fields? it should be the same
           for all the fields that are required." This used to hand the empty
           input to reportValidity() and let Chrome park its own "Please select a
           file." on it: a different shape, a different colour, in a different
           voice, and gone again the moment anything else was touched. It now says
           the page's own sentence in the page's own red, the box goes red with it,
           and the press is refused exactly as a missing size is. */
        this.printError('Please add your fingerprint to continue.');
        this.refuse(this.printBoxEl());
        return;
      }
    }

    var group = this.groupToken();               // before either route, so the ring carries it
    var extras = this.extras();
    var file = this.hasFile();
    if (!extras.length && !file) return;        // the theme's own add-to-cart, untouched

    e.preventDefault();
    e.stopPropagation();
    if (this.busy) return;
    this.busy = true;
    this.note('');
    this.loading(true);

    var self = this;
    var fd;
    try {
      fd = this.ringFormData();
    } catch (err) {
      this.busy = false; this.loading(false);
      this.note('Sorry - your ring could not be added. Please reload the page and try again.');
      return;
    }

    // ONCE THE RING IS IN, NOTHING MAY UNLOCK THE BUTTON. Before 19 October the
    // lock below was reached only when the extras' call answered JSON with a
    // bad status. A connection that dropped between the two calls, or a cart
    // that answered with a page instead of JSON (a 429, a bot check), fell
    // into the catch at the bottom, which said "try again" and re-enabled the
    // button - with the ring already in the cart, so the next press added a
    // second ring with a second token. This flag is set the moment the first
    // call comes back 2xx, before its body is even read, and the catch
    // honours it. (Shopify Theme (Office), 19 October 2026)
    var ringAdded = false;
    fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (r.ok) ringAdded = true;
      return r.json().then(function (j) { return { ok: r.ok, body: j }; });
    }).then(function (res) {
      if (!res.ok) {
        // Nothing was added, so the customer may fix it and press again.
        self.busy = false;
        self.loading(false);
        self.note(res.body.description || res.body.message || 'Sorry - your ring could not be added.');
        return;
      }
      if (!extras.length) { self.goToCart(); return; }

      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: extras.map(function (x) {
          var line = { id: x.id, quantity: x.quantity };
          if (x.grouped) line.properties = { _sp_group: group };
          return line;
        }) })
      }).then(function (r2) {
        return r2.json().then(function (j2) { return { ok: r2.ok, body: j2 }; });
      }).then(function (res2) {
        if (res2.ok) { self.goToCart(); return; }
        // THE RING IS IN AND SOMETHING ELSE IS NOT. Say so in plain words and
        // STOP: pressing again would add a second ring. The lock is only
        // cleared by reloading the page, which is the honest way out.
        self.locked = true;
        self.loading(true);
        var names = extras.map(function (x) { return x.what; }).join(' and ');
        self.note('Your ring is in the cart, but ' + names + ' could not be added. '
          + 'Please open your cart to check it, or contact us and we will add it for you.');
      });
    }).catch(function () {
      if (ringAdded) {
        // The ring is in and the rest never got an answer. Same lock as above;
        // only a reload clears it.
        self.locked = true;
        self.loading(true);
        var left = extras.map(function (x) { return x.what; }).join(' and ');
        self.note('Your ring is in the cart, but we could not confirm ' + (left || 'the rest') + '. '
          + 'Please open your cart to check it, or contact us and we will add it for you.');
        return;
      }
      self.busy = false;
      self.loading(false);
      self.note('Sorry - we could not reach the cart. Please check your connection and try again.');
    });
  };

  /* --------------------------------------------------------------- wire */

  Panel.prototype.start = function () {
    var self = this;
    var root = this.root;

    // ---- the engraving cards
    $$('[data-sp-card]', root).forEach(function (card) {
      card.addEventListener('click', function () {
        var which = card.getAttribute('data-sp-card');
        // ONE AREA OPEN AT A TIME: opening Text closes Fingerprint and the other
        // way round (Lorenzo, 14 October 2026).
        if (which === 'text') {
          if (self.isOpen('text')) {
            self.openArea('text', false);
            self.paintText();
          } else {
            self.openArea('print', false);
            self.openArea('text', true);
            // NO CURSOR IN THE BOX ON OPENING, so a phone does not throw its keyboard up.
            // Lorenzo, 17 October 2026: "I dont like that when I click on the text the keyboard
            // shows up automatically disrupting the experience". The box is the next thing after
            // the card, so a tap - or Tab on a keyboard - reaches it.
          }
          return;
        }
        if (which === 'print') {
          // TAPPING A CHOSEN CARD NO LONGER REMOVES THE PHOTO. It opens (or
          // closes) the area, which shows the photo's name with Change and
          // Remove; Remove is the only way a chosen photo goes, and the fee
          // goes with it, because the fee is only added when a file is there.
          var opening = !self.isOpen('print');
          if (opening) {
            self.openArea('text', false);
            self.paintText();
          }
          self.openArea('print', opening);
        }
      });
    });

    /* ---- INSIDE | OUTSIDE. The switch owns nothing but the chosen side: it sets
       it and calls paintSide, which is the one place that decides both the look and
       whether the order carries the property.

       THERE IS NOTHING TO WIRE ON A LABEL. Where only one side is allowed the markup
       draws no segments at all (option A, Lorenzo 17 October 2026), so this loop
       finds none and binds nothing.

       THE ARROW KEYS move the choice, the way they do in a radio group.

       THE aria-disabled LINE IN pick() IS A GUARD, NOT A PATH. Nothing renders a
       disabled segment any more - that was the greyed-out side he rejected - and it
       is kept only so that a mode which ever did draw one could not quietly select
       it. Do not read it as live behaviour. */
    $$('[data-sp-side]', root).forEach(function (group) {
      var which = group.getAttribute('data-sp-side');
      var opts = $$('[data-sp-side-opt]', group);
      var pick = function (opt) {
        if (!opt || opt.getAttribute('aria-disabled') === 'true') return false;
        self.side[which] = opt.getAttribute('data-sp-side-opt');
        self.paintSide(which);
        return true;
      };
      opts.forEach(function (opt) {
        opt.addEventListener('click', function () { pick(opt); });
        opt.addEventListener('keydown', function (ev) {
          var back = ev.key === 'ArrowLeft' || ev.key === 'ArrowUp';
          var fwd = ev.key === 'ArrowRight' || ev.key === 'ArrowDown';
          if (!back && !fwd) return;
          ev.preventDefault();
          var at = opts.indexOf(opt);
          var next = opts[(at + (fwd ? 1 : opts.length - 1)) % opts.length];
          if (pick(next)) next.focus();
        });
      });
    });

    var text = $('[data-sp-text]', root);
    if (text) {
      text.addEventListener('input', function () { self.paintText(); });
      // ENTER MUST NOT SUBMIT. In a form the Enter key submits, and the owner's
      // instruction is that it saves and closes instead.
      text.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.keyCode === 13) {
          ev.preventDefault();
          self.paintText();
          self.openArea('text', false);
        }
      });
      // On blur the card's look is brought up to date but the box is left open,
      // so that tapping the card to close it is not fighting the blur.
      text.addEventListener('blur', function () { self.paintText(); });
    }

    var print = $('[data-sp-print]', root);
    if (print) {
      print.addEventListener('change', function () {
        var f = print.files && print.files[0];
        // A CANCELLED PICKER keeps the photo there was (see restorePrint).
        if (!f) {
          if (self.lastPrint) self.restorePrint(print, self.lastPrint);
          self.paintPrint();
          return;
        }
        // IMAGES ONLY, ON BOTH KINDS OF CARD. The handover defers to "the
        // site's supported formats"; the site has only ever taken images, and
        // the crop tool the designer uses needs a picture. PDF was accepted on
        // the +$150 card for one build on 13 October and was taken out on the
        // 14th. A refusal leaves the area open so the next try is one tap away.
        var okType = /^image\//.test(f.type) || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(f.name);
        // A REFUSED FILE does not take the previous photo with it.
        var refuse = function (message) {
          self.clearPrint();
          if (self.lastPrint) { self.restorePrint(print, self.lastPrint); self.paintPrint(); }
          self.openArea('print', true);
          self.printError(message);
        };
        if (!okType) {
          refuse('That file type will not work. Please choose a JPG or PNG.');
          return;
        }
        if (f.size > MAX_FILE_BYTES) {
          refuse('That file is too big. Please choose one under 20 MB.');
          return;
        }
        self.lastPrint = f;
        self.printError('');
        self.paintPrint();
        self.openArea('print', false);
      });
    }

    // ---- DROPPING A PHOTO ON THE BOX
    //
    // Lorenzo, 16 October 2026: "Can you remove all the upload text make the ui
    // more obvious that is something you click or drop something in?" The box now
    // says "drop something in" with its look, so the drop has to be real and has
    // to be seen to happen.
    //
    // THE FILE INPUT ALREADY LAY OVER THE WHOLE BOX, so a dropped file landed on
    // it and the browser filled the input in by itself - MEASURED on this page:
    // the input is the topmost element at the centre of the box and at all four
    // corners. What the browser will NOT do is show anything while the file is
    // held over the box, and a box that does not react is a box you do not trust.
    //
    // So the drop is taken here instead: dragover is cancelled, the file is put
    // into that same input through a DataTransfer, and the input's own change
    // event is fired. EVERYTHING AFTER THAT POINT IS UNCHANGED - the type check,
    // the size cap, the refusal messages, the chosen-photo row, the fee line and
    // what reaches the order all run exactly as they do for a file picked from the
    // picker, because they are the same listener.
    //
    // NOTHING WIDER IS ACCEPTED. A dropped file is handed to that listener whole,
    // so a PDF dropped here is refused with the same words a PDF chosen from the
    // picker gets, rather than being swallowed in silence.
    //
    // WITHOUT DataTransfer there is nothing to put the file into, so the handlers
    // are not attached at all and the browser's own drop onto the input is left
    // alone. That is the right way round: no highlight, but the file still lands.
    var dropBox = print && print.closest ? print.closest('.sp-drop') : null;
    if (print && dropBox && typeof DataTransfer !== 'undefined') {
      // THE HIGHLIGHT MUST NOT GET STUCK ON. A gold box with nothing held over it
      // looks broken, and nothing on the page would ever clear it. So the lit
      // state is never left to one event arriving: the pointer's own position
      // decides a leave, and a watchdog catches the leaves that never come at
      // all - carrying the file out of the browser window, or letting go of it
      // somewhere else on the page, can cost the box its dragleave.
      var lastDragOver = 0;
      var dragWatch = null;
      var paintDrag = function (on) {
        dropBox.classList.toggle('is-dragover', !!on);
        if (!on && dragWatch) { clearInterval(dragWatch); dragWatch = null; }
      };
      // dragover fires over and over while a file is held still over the box, so
      // its last time is a heartbeat. 400ms of silence means the file has gone.
      var watchDrag = function () {
        lastDragOver = Date.now();
        if (dragWatch) return;
        dragWatch = setInterval(function () {
          if (Date.now() - lastDragOver > 400) paintDrag(false);
        }, 150);
      };
      // Only a drag actually carrying a file lights the box up. Dragging a word of
      // text or a link across the page must leave it alone.
      var dragHasFile = function (ev) {
        var types = ev.dataTransfer && ev.dataTransfer.types;
        if (!types) return false;
        for (var i = 0; i < types.length; i++) { if (types[i] === 'Files') return true; }
        return false;
      };
      dropBox.addEventListener('dragenter', function (ev) {
        if (!dragHasFile(ev)) return;
        ev.preventDefault();
        paintDrag(true);
        watchDrag();
      });
      dropBox.addEventListener('dragover', function (ev) {
        if (!dragHasFile(ev)) return;
        // Cancelling dragover is what makes this a drop target. It also stops the
        // browser's own last resort, which is to open the dropped photo in place
        // of the page - losing the ring, the size and the engraving with it.
        ev.preventDefault();
        if (ev.dataTransfer) { try { ev.dataTransfer.dropEffect = 'copy'; } catch (err) {} }
        paintDrag(true);
        watchDrag();
      });
      dropBox.addEventListener('dragleave', function (ev) {
        if (!dragHasFile(ev)) return;
        // dragleave ALSO fires when the pointer crosses onto something inside the
        // box, which is why this reads the pointer and not the event: still
        // inside means it never left. A leave that quits the window reports 0,0,
        // which is outside, so that one clears as it should.
        var box = dropBox.getBoundingClientRect();
        if (ev.clientX > box.left && ev.clientX < box.right &&
            ev.clientY > box.top && ev.clientY < box.bottom) return;
        paintDrag(false);
      });
      dropBox.addEventListener('drop', function (ev) {
        if (!dragHasFile(ev)) return;
        ev.preventDefault();
        paintDrag(false);
        var dropped = ev.dataTransfer && ev.dataTransfer.files;
        if (!dropped || !dropped.length) return;
        // ONE PHOTO. The input has no `multiple`, so the picker can only ever hand
        // it one; a drop is held to the same rule and takes the first.
        try {
          var dt = new DataTransfer();
          dt.items.add(dropped[0]);
          print.files = dt.files;
        } catch (err) {
          return;
        }
        // The same event the picker fires, so the same listener does the rest.
        print.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // REMOVE: the one way a chosen photo goes. The area stays open, so another
    // photo is one tap away.
    var removeBtn = $('[data-sp-remove]', root);
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        self.lastPrint = null;
        self.clearPrint();
        self.openArea('print', true);
      });
    }

    /* ---- THE SIZE SELECT. `required` comes OFF here and stays on in the markup.
       The browser's own validation runs BEFORE any submit listener, so with it on the
       customer got Chrome's bubble on the select instead of the guidance Lorenzo
       asked for, and our listener never ran at all. Taking it off in script means a
       page whose JavaScript failed still has the browser's refusal to fall back on,
       which is the safer way round. */
    var sizeSel = this.sizeSelect();
    if (sizeSel) {
      sizeSel.removeAttribute('required');
      sizeSel.addEventListener('change', function () {
        // "Once the customer selects a valid ring size: remove the instructional
        // message automatically." And nothing is added: they press again themselves.
        if (String(sizeSel.value || '').trim()) self.sizeFlag(false);
        /* AND THE BUTTON GOES BACK TO THE THEME'S OWN WORDS AND THE TOTAL.
           Called for a size being cleared as well as chosen, so the two can
           never disagree. */
        self.paintAtcLabel();
      });
    }

    // ---- the ring sizer
    var box = sizerBox(root);
    if (box) {
      box.addEventListener('change', function () {
        self.paintSizer();
        self.paintTimeline();
        /* The kit changes WHICH of the two sentences is true, so a message already on
           screen is rewritten to match. Only the words: no scroll, no focus, nothing
           flagged afresh - ticking the kit must never raise a NEW warning. */
        if (self.sizeFlagged()) self.sizeFlag(true, false);
      });
    }

    // ---- crafting time
    $$('[data-sp-speed]', root).forEach(function (input) {
      input.addEventListener('change', function () {
        self.paintSpeed();
        self.paintTimeline();
      });
    });

    // ---- the Priority switch. It owns nothing: it checks the other radio and
    // fires the change event the listener above already waits for, so one path
    // paints everything whether the switch, the keyboard or a ladder change moved
    // it. A <button> gives Space and Enter for free; role="switch" is what makes
    // it announce as on or off rather than as a press.
    var prio = $('[data-sp-priority]', root);
    if (prio) {
      prio.addEventListener('click', function () {
        var free = $('[data-sp-speed="free"]', root);
        var paid = $('[data-sp-speed="paid"]', root);
        if (!free || !paid) return;
        var next = paid.checked ? free : paid;
        next.checked = true;
        next.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // ---- the variant, which can change the ladder under everything
    document.addEventListener('variant:change', function (evt) {
      var v = evt.detail && evt.detail.variant;
      if (!v || !self.ladders || !self.ladders.variants) return;
      var key = self.ladders.variants[v.id];
      if (!key) return;                  // another product's event
      self.variantId = v.id;             // the total's ring price
      self.applyLadder(key);
      self.paintTimeline();
      self.paintTotal();
    });

    // ---- the total on Add to Cart follows the theme's own wording. When the theme
    // writes "Sold Out" or "Unavailable" into the button the total goes; when it
    // writes "Add to cart" back, the total returns. Watched rather than assumed,
    // because the theme may write it before or after the variant event above.
    var atcLabel = this.form && this.form.querySelector('[data-add-to-cart-text]');
    if (atcLabel && window.MutationObserver) {
      /* AND IT PUTS "CHOOSE YOUR SIZE" BACK IF THE THEME HAS WRITTEN OVER IT.
         paintAtcLabel only writes when the words really differ, so this settles
         after one pass instead of calling itself: write -> this observer ->
         paintAtcLabel sees the words it wanted -> nothing. */
      new MutationObserver(function () { self.paintAtcLabel(); self.paintTotal(); })
        .observe(atcLabel, { childList: true, characterData: true, subtree: true });
    }

    /* ---- THE TITLE ROW'S PRICE IS WATCHED, BUT NEVER WRITTEN. Since 19 October 2026
       nothing here paints it - the theme owns it. The watch is kept because the theme
       rewriting that price is a dependable sign that the variant has changed, and the
       BUTTON'S total has to be worked out again when it does. It cannot loop now for a
       plainer reason than before: paintTotal no longer writes to either element, so it
       cannot cause the mutation that would call it. */
    var topEls = [document.querySelector('[data-product-price]'),
                  document.querySelector('[data-compare-price]')].filter(Boolean);
    if (topEls.length && window.MutationObserver) {
      var topBusy = false;
      var topOb = new MutationObserver(function () {
        if (topBusy) return;
        topBusy = true;
        self.paintTotal();
        topBusy = false;
      });
      topEls.forEach(function (el) {
        topOb.observe(el, { childList: true, characterData: true, subtree: true });
      });
    }

    /* ---- THE STICKY ADD TO CART, phones only (the styles decide that; nothing here
       measures the screen). It appears ONLY once the real button has gone UP out of
       view, and not before the customer has reached it - which is the difference
       between a bar that helps and a bar that sits there from the first paint.

       IT IS LEFT WHERE IT IS DRAWN, and moved to <body> only if an ancestor would
       break position:fixed - see the walk below. MEASURED on this template: there is
       no such ancestor, so it stays in its section and is re-rendered with it. */
    /* THE PANEL'S OWN BAR, not whichever one is first in the document. In the
       theme editor a section is re-rendered and a second bar exists for a moment,
       and the one left in <body> by the section that has gone is the stale one -
       with handlers pointing at a button that is no longer on the page. Taking it
       from this panel's own root, and throwing away any bar already sitting in
       <body>, is what keeps the live one and the dead one apart. */
    var bar = $('[data-sp-sticky]', root) || $('[data-sp-sticky]');
    if (bar) {
      $$('[data-sp-sticky]').forEach(function (old) {
        if (old !== bar && old.parentNode === document.body) old.parentNode.removeChild(old);
      });
      /* IT IS LEFT WHERE IT IS DRAWN UNLESS SOMETHING ABOVE IT WOULD BREAK
         position:fixed. A transform, a filter, a perspective or a will-change on
         any ancestor makes a fixed box measure itself against THAT ancestor
         instead of the screen, and the bar would sit in the middle of the page.

         MOVING IT TO <body> ALWAYS, WHICH IS WHAT THIS DID FIRST, COSTS MORE THAN
         IT BUYS: out there it is no longer part of the section's own markup, so
         when the theme editor re-renders the section the bar is simply gone - seen
         while testing, a re-render left zero bars on the page. MEASURED on this
         template: the bar has NO transformed ancestor at all, so it stays put and
         is re-rendered with its section like everything else. The walk below is
         the insurance, for a theme or an app that adds one later. */
      var needsBody = false;
      for (var up = bar.parentNode; up && up.nodeType === 1 && up !== document.body; up = up.parentNode) {
        var ups = window.getComputedStyle(up);
        if (ups.transform !== 'none' || ups.filter !== 'none'
            || ups.perspective !== 'none' || ups.willChange !== 'auto') { needsBody = true; break; }
      }
      // Stamped with the root it belongs to, so that a bar left in <body> by a
      // section that has gone can be told from one whose panel is still alive, and
      // kept on the panel so it can still be found after it has been moved.
      bar.__sestoOwner = root;
      this.sticky = bar;
      if (needsBody && bar.parentNode !== document.body) document.body.appendChild(bar);
      var stickyBtn = $('[data-sp-sticky-btn]', bar);
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          var real = self.button();
          if (real && !real.disabled) real.click();   // the real path, every check on it
        });
      }
      /* HOW FAR OFF THE FOOT OF THE SCREEN TO SIT. Zero for a customer. In a
         preview or the theme editor Shopify puts its own bar across the bottom,
         and ours would be underneath it - MEASURED on the draft at 375: ours at
         738.3-812, Shopify's #PBarNextFrameWrapper at 744-812, and
         elementFromPoint in the middle of ours answering Shopify's iframe.

         ITS HEIGHT IS MEASURED, NEVER ASSUMED, and measured again on every pass,
         so it follows the bar changing size or going away. SHOPIFY'S BAR IS NEVER
         TOUCHED - we move out of its way, it does not move out of ours. It only
         counts when it really is at the foot of the screen; a bar somewhere else
         is not in our way. */
      var shopifyBarHeight = function () {
        var pb = document.getElementById('PBarNextFrameWrapper')
              || document.getElementById('PBarNextFrame');
        if (!pb) return 0;
        var cs = window.getComputedStyle(pb);
        if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
        var r = pb.getBoundingClientRect();
        if (r.height < 1 || r.height > window.innerHeight / 2) return 0;
        if (Math.abs(r.bottom - window.innerHeight) > 4) return 0;
        return Math.round(r.height);
      };
      var lift = function () {
        var h = shopifyBarHeight();
        var want = h ? h + 'px' : '';
        if (bar.style.bottom !== want) bar.style.bottom = want;
        return h;
      };
      var showBar = function (on) {
        var over = lift();
        // SHOWN FIRST, MEASURED SECOND. offsetHeight of a `hidden` element is 0, so
        // measuring before the bar is shown set the page's bottom padding to the lift
        // alone and the foot of the page sat under the bar the FIRST time it appeared
        // - every time after that it was right, which is what hid the bug.
        if (bar.hidden !== !on) bar.hidden = !on;
        if (on) {
          // the page's foot has to clear our bar AND whatever we are sitting on
          document.documentElement.style.setProperty('--sp-sticky-h', (bar.offsetHeight + over) + 'px');
        }
        // Unconditionally, on every pass: an early return here could leave the class
        // on <body> with no bar under it, and the page padded for nothing. The padding
        // itself is in the phone media query, so a wide screen gets none.
        document.body.classList.toggle('sp-has-sticky', on);
      };
      var realBtn = this.button();
      if (realBtn) {
        /* A SCROLL LISTENER AND A TIMER, rather than IntersectionObserver and
           requestAnimationFrame, and the reason is a measurement about the TEST
           HARNESS, not about those APIs.

           MEASURED while building this: in the preview browser used to test this
           page, nothing driven by rendering is delivered at all while the pane is
           not on screen - a fresh IntersectionObserver on a plain empty div never
           fired once, requestAnimationFrame never ran, and scroll events themselves
           were not delivered either (0 of them after scrolling the document to
           1800). Forcing a paint delivered all of it. So the choice here does not
           make the bar testable on its own; what it does is keep the work off the
           frame callbacks, which leaves one plain path - a scroll event, a timer, a
           getBoundingClientRect - that behaves the same in a real browser and in a
           harness, and is easy to reason about if it ever misbehaves.

           The timer fires at most every 50ms, and because the last scroll event
           always schedules one more pass, the bar settles on the true position
           rather than on the last one seen mid-scroll.

           GONE UP, NOT MERELY OUT OF VIEW: the bar shows when the button's FOOT is
           above the top of the screen. Below the screen means the customer has not
           reached it yet, which is not the same thing and shows nothing. */
        var pending = false;
        var check = function () {
          pending = false;
          // A section re-render in the theme editor leaves the old bar and the old
          // button off the page; its listener is still bound to window, so it stops
          // here rather than painting something nobody can see.
          if (!bar.isConnected || !realBtn.isConnected) return;
          showBar(realBtn.getBoundingClientRect().bottom <= 0);
        };
        var onScroll = function () {
          if (pending) return;
          pending = true;
          setTimeout(check, 50);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        check();
      }
      // The theme writing "Sold Out" into the button already reaches the mirror: the
      // observer above calls paintTotal, and paintTotal ends in paintSticky. A second
      // observer on the same span only did the same work twice.
      this.paintSticky();
    }

    // ---- add to cart
    document.addEventListener('submit', function (e) {
      if (e.target !== self.form) return;
      self.submit(e);
    }, true);

    this.paintSizer();
    this.paintSpeed();
    this.paintText();
    this.paintPrint();
    // BOTH UPLOAD AREAS START CLOSED, on every ring. openArea() also strips
    // `required` off a required input here, which is what makes the closed
    // state safe to submit from.
    this.openArea('print', false);
    this.paintTimeline();
    this.paintTotal();

    // ---- and again whenever the browser may have refilled the boxes behind our back:
    // after parsing, after load, and every time the page is shown (Back included).
    var resync = function () { self.resync(); };
    document.addEventListener('DOMContentLoaded', resync);
    window.addEventListener('load', resync);
    window.addEventListener('pageshow', resync);
  };

  // THE SPEC TABLE MOVES INTO "Specs". sections/product-specs.liquid still renders
  // it from the product's tags at the foot of the page; this only moves it, the way
  // the Tabs Studio app did before snippets/ring-pdp-details.liquid replaced it on
  // this template (14 October 2026). Each row's title loses its leading symbol
  // ("◎ Core" -> "Core") because the icon is drawn by ring-pdp-styles from the row's
  // class instead. A table with no rows leaves "Specs" hidden.
  function moveSpecs() {
    var item = $('[data-sp-specs]');
    var slot = item && $('[data-sp-specs-slot]', item);
    var table = $('.product-specs');
    if (!slot || !table || slot.contains(table)) return;
    var rows = $$('.specification', table);
    if (!rows.length) return;
    rows.forEach(function (row) {
      var t = $('.spec-title', row);
      if (t) t.textContent = t.textContent.replace(/^[^A-Za-z0-9]+/, '').trim();
    });
    slot.appendChild(table);
    item.hidden = false;
  }

  // ONE SECTION OPEN AT A TIME (Lorenzo, 14 October 2026). name="sp-details" on
  // the three <details> does it natively; this is for browsers older than that
  // attribute. "toggle" does not bubble, so it is caught on the way down.
  var detailsBound = false;
  function oneOpenAtATime() {
    if (detailsBound) return;
    detailsBound = true;
    document.addEventListener('toggle', function (e) {
      var opened = e.target;
      if (!opened || !opened.open || !opened.matches || !opened.matches('[data-sp-details] > details')) return;
      $$('details', opened.parentNode).forEach(function (other) {
        if (other !== opened && other.open) other.open = false;
      });
    }, true);
  }

  // THE POP-UPS. "How the Ring Sizer Works" (Learn more on the sizer card;
  // snippets/ring-pdp-sizer-info.liquid) and the fingerprint how-to video ("Watch how
  // to take a fingerprint" under the upload box; snippets/ring-pdp-print-video.liquid),
  // Lorenzo, 15 October 2026. showModal() puts a pop-up above the page and keeps the
  // keyboard inside it; Esc, the X and a tap on the dimmed page around it all close it.
  // Neither pop-up has a "Got it" any more - the video's went on 16 October 2026 and the
  // sizer's on the 18th - and nothing here had to change for that: this binds whatever
  // matches the close selector inside the pop-up, and it never named the button.
  // The tap that opens one is stopped, so Learn more can never reach the
  // sizer card's label and tick the sizer.
  function popup(dlg, openers, closeSel, onOpen, onShut) {
    if (!dlg || !openers.length || dlg.spBound) return;
    dlg.spBound = true;
    if (typeof dlg.showModal !== 'function') {
      openers.forEach(function (b) { b.hidden = true; });  // a browser older than <dialog>
      return;
    }
    var back = null;
    function open(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      back = document.activeElement;
      document.documentElement.classList.add('sp-lock');
      dlg.showModal();
      // A pop-up marked data-sp-focus-self takes the focus ITSELF, so the browser does
      // not ring its first button the moment it opens (the video pop-up's X - Lorenzo,
      // 17 October 2026: "The X has a branded color ring around it which should be
      // removed"). Tab still reaches the X, with its ring, for a keyboard user.
      if (dlg.hasAttribute('data-sp-focus-self')) {
        try { dlg.focus({ preventScroll: true }); } catch (err) { dlg.focus(); }
      }
      var scroller = $('.sp-info__scroll', dlg);
      if (scroller) scroller.scrollTop = 0;
      if (onOpen) onOpen(dlg);
    }
    // THE PAGE IS UNLOCKED AS IT CLOSES, not when the browser's "close" event arrives:
    // that event is queued, and a pop-up reopened before it landed was left with the
    // page behind it scrolling - MEASURED. The event stays as the fallback for any
    // other way a browser shuts it, and it only unlocks a pop-up that really is shut.
    function settle() {
      if (dlg.open) return;
      document.documentElement.classList.remove('sp-lock');
      if (onShut) onShut(dlg);
      if (back && back.focus) { try { back.focus({ preventScroll: true }); } catch (err) { back.focus(); } }
      back = null;
    }
    function shut() { if (dlg.open) dlg.close(); settle(); }
    openers.forEach(function (b) { b.addEventListener('click', open); });
    $$(closeSel, dlg).forEach(function (b) { b.addEventListener('click', shut); });
    // The dialog box itself is only ever the target on the dimmed backdrop around the
    // panel - every visible part of the pop-up is the panel inside it.
    dlg.addEventListener('click', function (e) { if (e.target === dlg) shut(); });
    // Esc, and a phone's back gesture, first send "cancel"; it is shut there, so the
    // page unlocks at once - Chrome sends "close" only with the next painted frame.
    dlg.addEventListener('cancel', shut);
    dlg.addEventListener('close', settle);
  }

  function sizerInfo() {
    popup($('[data-sp-sizer-info]'), $$('[data-sp-sizer-info-open]'), '[data-sp-sizer-info-close]');
  }

  // THE VIDEO LOADS ONLY WHEN ITS POP-UP OPENS: it waits in a <template>, is copied into
  // the frame the first time, and plays from the start each time (inside the tap that
  // opened it, so a browser lets it play). It is paused when the pop-up closes.
  function printVideo() {
    popup($('[data-sp-print-video]'), $$('[data-sp-print-video-open]'), '[data-sp-print-video-close]',
      function (dlg) {
        var frame = $('[data-sp-video-frame]', dlg);
        var tpl = $('template[data-sp-video-src]', dlg);
        var video = frame ? $('video', frame) : null;
        if (!video && frame && tpl && tpl.content) {
          frame.appendChild(tpl.content.cloneNode(true));
          video = $('video', frame);
        }
        if (!video) return;
        try { video.currentTime = 0; } catch (err) { /* not loaded yet: it starts at 0 */ }
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () { /* controls are there */ });
      },
      function (dlg) {
        var video = $('[data-sp-video-frame] video', dlg);
        if (video && !video.paused) video.pause();
      });
  }

  // INSTAGRAM: the profile embed under the three folding sections
  // (snippets/ring-pdp-instagram.liquid; Lorenzo, 15 October 2026). Nothing of
  // Instagram's loads with the page: when the customer scrolls within 600px of it, the
  // blockquote is put in from its <template> and Instagram's embed.js is loaded once,
  // which turns it into the live profile card. A browser with no IntersectionObserver
  // loads it straight away.
  function instagramEmbed() {
    var box = $('[data-sp-insta-embed]');
    var tpl = $('template[data-sp-insta-src]');
    if (!box || !tpl || box.spBound) return;
    box.spBound = true;
    function load() {
      if (box.spLoaded) return;
      box.spLoaded = true;
      if (tpl.content) box.appendChild(tpl.content.cloneNode(true));
      var render = function () {
        if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();
      };
      if (window.instgrm) { render(); return; }
      var script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      script.onload = render;
      document.body.appendChild(script);
    }
    if (!('IntersectionObserver' in window)) { load(); return; }
    var watch = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { watch.disconnect(); load(); return; }
      }
    }, { rootMargin: '600px 0px' });
    watch.observe(box);
  }


  /* SHOP PAY'S INSTALLMENTS LINE, IN ITS SHORT FORM. Lorenzo, 17 October 2026, pointing at The
     Essence on the live theme: "Can you make it shorter and just say from.. like this". Shopify
     picks the wording by PRICE - "From $124.56/mo with shop" on a $1,380 ring, "4 interest-free
     installments, or from $49.55/mo with shop" on a $549 one (both MEASURED) - and no setting
     chooses between them. Its element keeps its text in an open shadow root, so the opening
     words are swapped for "From " and NOTHING ELSE is touched: the amount, the Shop logo, the
     (i) and the plans window it opens stay Shopify's own.
     SAFE IF SHOPIFY CHANGES IT: only an exact "N interest-free installments, or from" text node
     at the very start is rewritten. Anything else - another wording, another language, a new
     structure - is left as Shopify drew it. Shopify redraws the line on every variant change, so
     an observer re-applies it; the rewritten text no longer matches, so it cannot loop. */
  function shortInstallments() {
    var host = $('.sp-payment-terms shopify-payment-terms');
    if (!host) return;
    /* ONCE PER ELEMENT. boot() runs again on every shopify:section:load, and without
       this each tweak in the theme editor started another interval and hung another
       observer on Shop Pay's shadow root. The flag is on the ELEMENT, not a variable,
       so a re-rendered element is picked up exactly once and the old one goes with
       the markup it was in. */
    if (host.__sestoShortened) return;
    host.__sestoShortened = true;
    var LONG = /^\s*\d+\s+interest-free installments, or from\s*$/i;
    var fix = function () {
      var root = host.shadowRoot;
      var content = root && root.getElementById ? root.getElementById('shopify-installments-content') : null;
      var first = content && content.firstChild;
      if (first && first.nodeType === 3 && LONG.test(first.nodeValue)) first.nodeValue = 'From ';
    };
    var tries = 0;
    var watch = setInterval(function () {
      tries += 1;
      if (host.shadowRoot && window.MutationObserver) {
        clearInterval(watch);
        fix();
        new MutationObserver(fix).observe(host.shadowRoot, { childList: true, characterData: true, subtree: true });
      } else if (tries > 40) {
        clearInterval(watch);          // 20 seconds and no element: leave Shopify's line alone
      }
    }, 500);
  }

  function boot() {
    shortInstallments();
    moveSpecs();
    oneOpenAtATime();
    sizerInfo();
    printVideo();
    instagramEmbed();
    // THE REVIEWS ARE THE SHARED SCRIPT'S NOW - assets/sp-reviews.js, which
    // sections/sesto-fera-reviews-wrapper.liquid loads for this template and for the Ring
    // Sizer page. It starts itself when the document is ready, so this call is only here
    // to cover the case where that happened before the band existed; feraReviews marks the
    // band and returns early, so calling twice costs nothing. If the file is absent this
    // does nothing and the page is simply the page without the cut.
    if (window.SestoReviews && typeof window.SestoReviews.start === 'function') {
      window.SestoReviews.start();
    }
    $$('[data-sp-root]').forEach(function (root) {
      if (root.__sestoStarted) return;
      root.__sestoStarted = true;
      var panel = new Panel(root);
      if (!panel.form) return;
      panel.start();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('page:loaded', boot);

  /* AND WHEN THE THEME EDITOR REDRAWS A SECTION. Until 17 October 2026 nothing
     here listened for this, so in admin > Customize a re-rendered section was
     never started at all - not the sticky bar, and not the cards, the Priority
     switch or the totals either, because the new [data-sp-root] had no
     __sestoStarted on it and boot() was never called again. Lorenzo: the sticky
     bar "does not seam to work", in the editor as well as on his phone.

     The bar a dead section left in <body> is cleared out first, so the fresh
     panel moves its own one out and there is never a stale one underneath. */
  /* ONLY A BAR WHOSE PANEL HAS GONE. The first version of this deleted every bar
     sitting in <body>, which meant that ANY section loading - a footer, a banner,
     anything - took the ring's sticky bar away for good, because boot() will not
     rebuild a root it has already started. A bar is stamped with the root it belongs
     to when it is moved, so a bar whose root is still on the page is left alone. */
  function dropMovedBars() {
    $$('[data-sp-sticky]').forEach(function (b) {
      if (b.parentNode !== document.body) return;
      var owner = b.__sestoOwner;
      if (!owner || !owner.isConnected) b.parentNode.removeChild(b);
    });
  }
  /* And the class never outlives the bar: with no bar showing, the page keeps a
     bottom padding for something that is not there. */
  function tidySticky() {
    dropMovedBars();
    var showing = $$('[data-sp-sticky]').some(function (b) { return !b.hidden; });
    if (!showing) document.body.classList.remove('sp-has-sticky');
  }
  document.addEventListener('shopify:section:load', function () {
    tidySticky();
    boot();
  });
  document.addEventListener('shopify:section:unload', tidySticky);
})();
